import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import useMixpanelEvent from "./useMixpanelEvent";
import { User } from "@/types";
import { logger } from "@/services/LoggerService";

const STORAGE_KEY = "mixpanel-viewed-properties";

interface Property {
    id: string | number;
}

interface UseMixpanelPageViewTrackingOptions {
    targetRef: React.RefObject<HTMLElement>;
    property: Property;
    pageName: string;
    userState: User | null;
    visitorStatus: string;
    feedType?: string;
    propertyUrl?: string;
    threshold?: number;
    debounceDelay?: number;
    immediate?: boolean;
}

/**
 * Custom hook for tracking Mixpanel page views with IntersectionObserver and localStorage deduplication.
 *
 * This hook automatically tracks page views when a user scrolls past a property and becomes fully visible.
 * It includes deduplication logic to ensure only one page view event is sent per property per user,
 * even across browser sessions.
 *
 * @param {Object} options - Configuration options for the page view tracking
 * @param {React.RefObject} options.targetRef - React ref object pointing to the element to observe for visibility
 * @param {Object} options.property - Property object containing the unique identifier
 * @param {string|number} options.property.id - Unique property identifier used for deduplication
 * @param {string} options.pageName - Name of the page/section for analytics (e.g., "Property Feed - Trending", "Property Details")
 * @param {Object} options.userState - User state object containing authentication information
 * @param {string|number|null} options.userState.id - User identifier (null for unauthenticated users)
 * @param {string|null} options.userState.nickname - User's nickname (null for unauthenticated users)
 * @param {string|null} options.userState.email - User's email address (null for unauthenticated users)
 * @param {string} options.visitorStatus - Visitor status for analytics (e.g., "new-guest", "repeat-guest", "registered")
 * @param {string} [options.feedType] - Type of feed (optional, for property feeds like "trending" or "following")
 * @param {string} [options.propertyUrl] - URL of the property (optional, for detail pages)
 * @param {number} [options.threshold=1.0] - IntersectionObserver threshold (0.0 to 1.0). 1.0 means 100% visible
 * @param {number} [options.debounceDelay=100] - Delay in milliseconds before sending the event to prevent rapid-fire events
 * @param {boolean} [options.immediate=false] - If true, fire event as soon as property is loaded
 *
 * @returns {void} This hook doesn't return any values
 *
 * @description
 * This hook provides the following features:
 *
 * 1. **IntersectionObserver Integration**: Automatically detects when the target element becomes visible
 * 2. **Deduplication**: Uses localStorage to prevent duplicate events for the same property
 * 3. **Debouncing**: Prevents rapid-fire events during fast scrolling
 * 4. **Automatic Cleanup**: Manages localStorage size by removing old entries
 * 5. **Error Handling**: Gracefully handles localStorage errors
 * 6. **Performance Optimized**: Uses memoization to prevent unnecessary re-renders
 * 7. **User Information**: Includes user_id, nickname, and email in analytics data
 *
 * The hook stores viewed property IDs in localStorage under the key 'mixpanel-viewed-properties'
 * and automatically cleans up old entries when the list exceeds 1000 items.
 *
 * The event data sent to Mixpanel includes:
 * - user_id: User identifier
 * - nickname: User's nickname
 * - email: User's email address
 * - isAuthenticated: Authentication status
 * - visitorStatus: Visitor status
 * - pageName: Page/section name
 * - propertyUrl: Property URL (if provided)
 *
 * @since 1.0.0
 * @author PriceMe Team
 */
const useMixpanelPageViewTracking = ({
    targetRef,
    property,
    pageName,
    userState,
    visitorStatus,
    feedType,
    propertyUrl,
    threshold = 1.0,
    debounceDelay = 100,
    immediate = false,
}: UseMixpanelPageViewTrackingOptions): void => {
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const hasTrackedRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const location = useLocation();
    const prevFeedTypeRef = useRef(feedType);
    const prevPathnameRef = useRef(location.pathname);

    // Helper function to manage viewed properties in localStorage
    const manageViewedProperties = useCallback((propertyId: string | number): boolean => {
        const MAX_PROPERTIES = 1000; // Limit to prevent localStorage from growing too large

        try {
            const viewedProperties = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

            // Check if property has already been viewed
            if (viewedProperties.includes(propertyId)) {
                return false; // Already viewed
            }

            // Add new property to the list
            viewedProperties.push(propertyId);

            // Clean up old entries if list gets too long (keep only the most recent 500)
            if (viewedProperties.length > MAX_PROPERTIES) {
                viewedProperties.splice(0, viewedProperties.length - 500);
            }

            // Save back to localStorage
            localStorage.setItem(STORAGE_KEY, JSON.stringify(viewedProperties));
            return true; // New property, should track
        } catch (error) {
            console.error("Error managing viewed properties:", error);
            logger.error("Error managing viewed properties", error, {
                context: "useMixpanelPageViewTracking",
                propertyId: String(propertyId),
            });
            // If there's an error, clear the storage and allow tracking
            localStorage.removeItem(STORAGE_KEY);
            return true;
        }
    }, []);

    // Clear viewed properties when feedType or URL changes
    useEffect(() => {
        const prevFeedType = prevFeedTypeRef.current;
        const prevPathname = prevPathnameRef.current;

        if ((feedType && prevFeedType !== feedType) || location.pathname !== prevPathname) {
            localStorage.removeItem(STORAGE_KEY);
        }

        prevFeedTypeRef.current = feedType;
        prevPathnameRef.current = location.pathname;
        // Reset tracked ref so new properties can be tracked after change
        hasTrackedRef.current = false;
    }, [feedType, location.pathname]);

    // Memoize the tracking function to prevent unnecessary re-renders
    const trackPageView = useCallback(() => {
        if (!hasTrackedRef.current) {
            // Only track if this property hasn't been viewed before
            if (manageViewedProperties(property?.id)) {
                hasTrackedRef.current = true;

                const eventData: any = {
                    isAuthenticated: !!userState?.id,
                    visitorStatus: !!userState?.id ? undefined : visitorStatus || "new-guest",
                    pageName,
                    user_id: userState?.id || null,
                    nickname: userState?.nickname || null,
                    email: userState?.emailAddress || null,
                };

                // Add property URL if provided (for detail pages)
                if (propertyUrl) {
                    eventData.propertyUrl = propertyUrl;
                }

                sendMixpanelEvent("Page View", eventData);
            }
        }
    }, [
        userState?.id,
        userState?.nickname,
        userState?.emailAddress,
        visitorStatus,
        sendMixpanelEvent,
        property?.id,
        manageViewedProperties,
        pageName,
        propertyUrl,
    ]);

    // Fire immediately if requested (for property details page)
    useEffect(() => {
        if (immediate && property && !hasTrackedRef.current) {
            trackPageView();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [immediate, property]);

    // Set up IntersectionObserver for page view tracking (for feeds)
    useEffect(() => {
        if (immediate) return; // Don't use observer if immediate
        const node = targetRef.current;
        if (!node || !property) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        // Element is fully visible on screen (user has scrolled past previous content)
                        // Add a small delay to prevent rapid-fire events
                        timeoutRef.current = setTimeout(() => {
                            trackPageView();
                        }, debounceDelay);
                        // Stop observing after first view
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold }, // Trigger when specified threshold of the element is visible
        );

        observer.observe(node);

        return () => {
            observer.unobserve(node);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property, trackPageView, threshold, debounceDelay, immediate]); // targetRef is stable, no need to include in deps
};

export default useMixpanelPageViewTracking;
